import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import PropTypes from "prop-types";
import {
    Alert,
    Box,
    Card,
    Container,
    Divider,
    FormControl,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    TablePagination,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import Skeleton from '@mui/material/Skeleton';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import GroupsIcon from "@mui/icons-material/Groups";
import moment from "moment";
import Groupmodify from "src/pages/Management/Groupmodify";
import Error403 from "../Error/403";

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box p={3}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired,
};

// Group type configuration
const GROUP_TYPE_MAP = {
    left: { label: 'All', key: 'all' },
    center: { label: 'Admin', key: 'admin' },
    right: { label: 'OPS', key: 'ops' },
    justify: { label: 'Read-Only', key: 'readonly' },
    trainer: { label: 'Trainer', key: 'trainer' },
    trainee: { label: 'Trainee', key: 'trainee' },
};

export default function Groups() {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [adminuserlist, setadminuserlist] = useState([]);
    const [opsuserlist, setopsuserlist] = useState([]);
    const [readonlyuserlist, setreadonlyuserlist] = useState([]);
    const [traineruserlist, setTraineruserlist] = useState([]);
    const [traineeuserlist, setTraineeuserlist] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination states for each group type
    const [adminpage, setAdminPage] = useState(0);
    const [adminrowsPerPage, setAdminRowsPerPage] = useState(10);
    const [opspage, setOpsPage] = useState(0);
    const [opsrowsPerPage, setOpsRowsPerPage] = useState(10);
    const [readonlypage, setReadyOnlyPage] = useState(0);
    const [readonlyrowsPerPage, setReadOnlyRowsPerPage] = useState(10);
    const [trainerpage, setTrainerPage] = useState(0);
    const [trainerrowsPerPage, setTrainerRowsPerPage] = useState(10);
    const [traineepage, setTraineePage] = useState(0);
    const [traineerowsPerPage, setTraineeRowsPerPage] = useState(10);

    // Fetch all user lists
    useEffect(() => {
        const fetchAllUserLists = async () => {
            setLoading(true);
            setError(null);
            
            try {
                const [adminRes, opsRes, readonlyRes, trainerRes, traineeRes] = await Promise.all([
                    fetch(`${process.env.REACT_APP_API_URL}/api/user_management/adminuserlistView/`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + localStorage.getItem("token"),
                },
                    }),
                    fetch(`${process.env.REACT_APP_API_URL}/api/user_management/opsuserlistView/`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + localStorage.getItem("token"),
                },
                    }),
                    fetch(`${process.env.REACT_APP_API_URL}/api/user_management/readonlyuserlistView/`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + localStorage.getItem("token"),
                },
                    }),
                    fetch(`${process.env.REACT_APP_API_URL}/api/user_management/traineruserlistView/`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + localStorage.getItem("token"),
                },
                    }),
                    fetch(`${process.env.REACT_APP_API_URL}/api/user_management/traineeuserlistView/`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + localStorage.getItem("token"),
                },
                    }),
                ]);

                const [adminData, opsData, readonlyData, trainerData, traineeData] = await Promise.all([
                    adminRes.json(),
                    opsRes.json(),
                    readonlyRes.json(),
                    trainerRes.json(),
                    traineeRes.json(),
                ]);

                setadminuserlist(adminData);
                setopsuserlist(opsData);
                setreadonlyuserlist(readonlyData);
                setTraineruserlist(trainerData);
                setTraineeuserlist(traineeData);
            } catch (err) {
                setError(err.message || "An error occurred while loading group data");
            } finally {
                setLoading(false);
            }
        };

        fetchAllUserLists();
    }, []);


    const [userlist, setusers] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchitems, setSearchItems] = useState('');

    useEffect(() => {
        const fetchUserList = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user_management/user_list/`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + localStorage.getItem("token"),
            },
                });
                
                if (!response.ok) {
                    throw new Error("Failed to fetch user list");
                }
                
                const data = await response.json();
                if (data.detail) {
                    localStorage.setItem("user_access", data.detail);
                } else {
                    setusers(data);
                }
            } catch (err) {
                // Handle error silently or show user-friendly message
            }
        };
        
        fetchUserList();
    }, []);

    const [value, setValue] = React.useState(0);
    const [alignment, setAlignment] = React.useState("left");
    const [labTypeView, setLabTypeView] = useState("left");

    const handleChange1 = useCallback((event) => {
        const alignmentType = event.target.value;
        setAlignment(alignmentType);
        setLabTypeView(alignmentType);
    }, []);
    // Memoized filter function
    const filterUsers = useCallback((userList) => {
        if (!userList || !Array.isArray(userList)) return [];
        
        if (!searchitems) {
            return userList;
        }
        
        const searchTerm = searchitems.toLowerCase();
        return userList.filter((user) => {
            const email = user.email?.toLowerCase() || '';
            const username = user.username?.toLowerCase() || '';
            return email.includes(searchTerm) || username.includes(searchTerm);
        });
    }, [searchitems]);

    // Memoized filtered lists
    const filteredUserList = useMemo(() => filterUsers(userlist), [userlist, filterUsers]);
    const filteredAdminUserList = useMemo(() => filterUsers(adminuserlist), [adminuserlist, filterUsers]);
    const filteredOpsUserList = useMemo(() => filterUsers(opsuserlist), [opsuserlist, filterUsers]);
    const filteredReadOnlyUserList = useMemo(() => filterUsers(readonlyuserlist), [readonlyuserlist, filterUsers]);
    const filteredTrainerUserList = useMemo(() => filterUsers(traineruserlist), [traineruserlist, filterUsers]);
    const filteredTraineeUserList = useMemo(() => filterUsers(traineeuserlist), [traineeuserlist, filterUsers]);

    // Memoized paginated lists
    const paginatedUserList = useMemo(() => 
        filteredUserList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filteredUserList, page, rowsPerPage]
    );
    const paginatedAdminUserList = useMemo(() => 
        filteredAdminUserList.slice(adminpage * adminrowsPerPage, adminpage * adminrowsPerPage + adminrowsPerPage),
        [filteredAdminUserList, adminpage, adminrowsPerPage]
    );
    const paginatedOpsUserList = useMemo(() => 
        filteredOpsUserList.slice(opspage * opsrowsPerPage, opspage * opsrowsPerPage + opsrowsPerPage),
        [filteredOpsUserList, opspage, opsrowsPerPage]
    );
    const paginatedReadOnlyUserList = useMemo(() => 
        filteredReadOnlyUserList.slice(readonlypage * readonlyrowsPerPage, readonlypage * readonlyrowsPerPage + readonlyrowsPerPage),
        [filteredReadOnlyUserList, readonlypage, readonlyrowsPerPage]
    );
    const paginatedTrainerUserList = useMemo(() => 
        filteredTrainerUserList.slice(trainerpage * trainerrowsPerPage, trainerpage * trainerrowsPerPage + trainerrowsPerPage),
        [filteredTrainerUserList, trainerpage, trainerrowsPerPage]
    );
    const paginatedTraineeUserList = useMemo(() => 
        filteredTraineeUserList.slice(traineepage * traineerowsPerPage, traineepage * traineerowsPerPage + traineerowsPerPage),
        [filteredTraineeUserList, traineepage, traineerowsPerPage]
    );

    // Reset pagination when search changes
    useEffect(() => {
        setPage(0);
        setAdminPage(0);
        setOpsPage(0);
        setReadyOnlyPage(0);
        setTrainerPage(0);
        setTraineePage(0);
    }, [searchitems]);

    // Memoized DataGrid columns
    const columns = useMemo(() => [
        { field: 'username', headerName: 'Name', flex: 1, minWidth: 150, headerAlign: 'center', align: 'center' },
        { field: 'email', headerName: 'Email', flex: 1.2, minWidth: 200, headerAlign: 'center', align: 'center' },
        { field: 'created_at', headerName: 'Created At', flex: 0.8, minWidth: 150, headerAlign: 'center', align: 'center', valueFormatter: (params) => moment(params.value).format('DD/MM/YYYY') },
        { field: 'action', headerName: 'Action', flex: 0.7, minWidth: 140, headerAlign: 'center', align: 'center', renderCell: (params) => <Groupmodify UserID={params.row.id} /> }
    ], []);

    const handleSearchChange = useCallback((e) => {
        setSearchItems(e.target.value);
    }, []);

    const handleClearSearch = useCallback(() => {
        setSearchItems("");
    }, []);
    return (
        <React.Fragment>
            <Helmet>
                <title>Manage Groups - LabAsService</title>
            </Helmet>
            {localStorage.getItem("user_access") ===
                "You do not have permission to perform this action." &&
                localStorage.getItem("group") !== "admin" ? (
                <Error403 />
            ) : (
                <Container maxWidth={false} sx={{ py: 6, pb: 8, px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 } }}>
                    <Card sx={{ mb: 4 }}>
                        {/* Hero Header */}
                        <Box
                            sx={{
                                background: 'linear-gradient(135deg, rgba(46,125,50,0.12), rgba(21,101,192,0.12))',
                                borderTopLeftRadius: 12,
                                borderTopRightRadius: 12,
                                p: { xs: 2.5, md: 3 },
                            }}
                        >
                            <Stack direction="row" spacing={2} alignItems="center">
                                <GroupsIcon color="primary" sx={{ fontSize: 32 }} />
                                <Box>
                                    <Typography variant="h4" component="h1">
                            Manage Groups
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        View and manage users by group type (Admin, OPS, Read-Only, Trainer, Trainee)
                                    </Typography>
                                </Box>
                            </Stack>
                                </Box>
                                
                        <Divider />

                        {/* Error State */}
                        {error && (
                            <Box sx={{ p: { xs: 2.5, md: 3 }, pb: 0 }}>
                                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                                    {error}
                                </Alert>
                            </Box>
                        )}

                        <TabPanel value={value} index={0}>
                            <Box sx={{ p: { xs: 2.5, md: 3 } }}>
                                {/* Controls Section */}
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={2}
                                    alignItems={{ xs: 'stretch', sm: 'center' }}
                                    justifyContent="space-between"
                                    sx={{ mb: 3 }}
                                >
                                    <Stack direction="row" spacing={2} alignItems="center">
                                    <Typography
                                            variant="h6" 
                                            sx={{ 
                                                fontSize: { xs: '1.1rem', sm: '1.25rem' }, 
                                                fontWeight: 700,
                                                color: 'text.primary',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            Group Type
                                    </Typography>
                                        <FormControl size="small" sx={{ minWidth: 140 }}>
                                            <Select
                                                value={labTypeView}
                                                onChange={handleChange1}
                                                sx={{
                                                    backgroundColor: 'background.paper',
                                                }}
                                            >
                                                {Object.entries(GROUP_TYPE_MAP).map(([key, config]) => (
                                                    <MenuItem key={key} value={key}>
                                                        {config.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Stack>
                                    <TextField
                                        placeholder="Search by Email or Username"
                                        value={searchitems}
                                        onChange={handleSearchChange}
                                        size="small"
                                        sx={{
                                            width: '100%',
                                            maxWidth: { xs: '100%', sm: 400, md: 500 },
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon />
                                                </InputAdornment>
                                            ),
                                            endAdornment: searchitems && (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleClearSearch}
                                                        aria-label="clear search"
                                                    >
                                                        <ClearIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Stack>

                                {/* Render DataGrid based on selected group type */}
                                {loading ? (
                                    <Box sx={{ width: '100%', mt: 2 }}>
                                        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
                                    </Box>
                                ) : (() => {
                                    let currentRows = [];
                                    let currentPage = 0;
                                    let currentRowsPerPage = 10;
                                    let currentFilteredList = [];
                                    let currentSetPage = () => {};
                                    let currentSetRowsPerPage = () => {};
                                    let isActiveFilter = (user) => true;

                                    if (alignment === "left") {
                                        currentRows = paginatedUserList;
                                        currentPage = page;
                                        currentRowsPerPage = rowsPerPage;
                                        currentFilteredList = filteredUserList;
                                        currentSetPage = setPage;
                                        currentSetRowsPerPage = setRowsPerPage;
                                        isActiveFilter = (user) => user.is_active === true || String(user.is_active).toLowerCase() === "true";
                                    } else if (alignment === "center") {
                                        currentRows = paginatedAdminUserList;
                                        currentPage = adminpage;
                                        currentRowsPerPage = adminrowsPerPage;
                                        currentFilteredList = filteredAdminUserList;
                                        currentSetPage = setAdminPage;
                                        currentSetRowsPerPage = setAdminRowsPerPage;
                                        isActiveFilter = (user) => user.is_active === "True" || user.is_active === true;
                                    } else if (alignment === "right") {
                                        currentRows = paginatedOpsUserList;
                                        currentPage = opspage;
                                        currentRowsPerPage = opsrowsPerPage;
                                        currentFilteredList = filteredOpsUserList;
                                        currentSetPage = setOpsPage;
                                        currentSetRowsPerPage = setOpsRowsPerPage;
                                        isActiveFilter = (user) => user.is_active === "True" || user.is_active === true;
                                    } else if (alignment === "justify") {
                                        currentRows = paginatedReadOnlyUserList;
                                        currentPage = readonlypage;
                                        currentRowsPerPage = readonlyrowsPerPage;
                                        currentFilteredList = filteredReadOnlyUserList;
                                        currentSetPage = setReadyOnlyPage;
                                        currentSetRowsPerPage = setReadOnlyRowsPerPage;
                                        isActiveFilter = (user) => user.is_active === "True" || user.is_active === true;
                                    } else if (alignment === "trainer") {
                                        currentRows = paginatedTrainerUserList;
                                        currentPage = trainerpage;
                                        currentRowsPerPage = trainerrowsPerPage;
                                        currentFilteredList = filteredTrainerUserList;
                                        currentSetPage = setTrainerPage;
                                        currentSetRowsPerPage = setTrainerRowsPerPage;
                                        isActiveFilter = (user) => user.is_active === "True" || user.is_active === true;
                                    } else if (alignment === "trainee") {
                                        currentRows = paginatedTraineeUserList;
                                        currentPage = traineepage;
                                        currentRowsPerPage = traineerowsPerPage;
                                        currentFilteredList = filteredTraineeUserList;
                                        currentSetPage = setTraineePage;
                                        currentSetRowsPerPage = setTraineeRowsPerPage;
                                        isActiveFilter = (user) => user.is_active === "True" || user.is_active === true;
                                    }

                                    const activeRows = currentRows.filter(isActiveFilter);

                                    return (
                                        <Card sx={{ mt: 2 }}>
                                            <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                                {activeRows.length > 0 ? (
                                                    <>
                                                        <DataGrid
                                                            rows={activeRows}
                                                            columns={columns}
                                                            hideFooter={true}
                                                            hideFooterPagination={true}
                                                            autoHeight
                                                            sx={{
                                                                '& .MuiDataGrid-row:hover': {
                                                                    backgroundColor: 'action.hover',
                                                                },
                                                                '& .MuiDataGrid-cell': {
                                                                    borderBottom: '1px solid',
                                                                    borderColor: 'divider',
                                                                },
                                                                '& .MuiDataGrid-columnHeaders': {
                                                                    backgroundColor: 'grey.50',
                                                                    fontWeight: 600,
                                                                },
                                                            }}
                                                        />
                                                            <TablePagination
                                                                rowsPerPageOptions={[10, 25, 50, 75, 100]}
                                                                component="div"
                                                            count={currentFilteredList.filter(isActiveFilter).length}
                                                            rowsPerPage={currentRowsPerPage}
                                                            page={currentPage}
                                                            onPageChange={(e, newPage) => currentSetPage(newPage)}
                                                                onRowsPerPageChange={(e) => {
                                                                currentSetRowsPerPage(parseInt(e.target.value, 10));
                                                                currentSetPage(0);
                                                            }}
                                                            sx={{
                                                                '& .MuiTablePagination-toolbar': {
                                                                    justifyContent: 'flex-start',
                                                                    paddingLeft: 2,
                                                                },
                                                                '& .MuiTablePagination-spacer': {
                                                                    display: 'none',
                                                                },
                                                                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                                                    margin: 0,
                                                                },
                                                            }}
                                                        />
                                                    </>
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            py: 8,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <GroupsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                                                        <Typography variant="h6" color="text.secondary" gutterBottom>
                                                            {searchitems ? 'No users found' : 'No users available'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {searchitems
                                                                ? `No users match your search "${searchitems}"`
                                                                : `No ${GROUP_TYPE_MAP[alignment]?.label || 'users'} found in this group.`}
                                                        </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                </Card>
                                    );
                                })()}
                            </Box>
                        </TabPanel>
                    </Card>
                </Container>
            )}
        </React.Fragment>
    );
}
